import React, { useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
// import the core library.
import ReactEChartsCore from 'echarts-for-react/lib/core'
// Import the echarts core module, which provides the necessary interfaces for using echarts.
import * as echarts from 'echarts-6/core'
// Import charts, all with Chart suffix
import {
    LineChart,
    BarChart,
    // PieChart,
    // ScatterChart,
    // RadarChart,
    // MapChart,
    // TreeChart,
    // TreemapChart,
    // GraphChart,
    // GaugeChart,
    // FunnelChart,
    // ParallelChart,
    // SankeyChart,
    // BoxplotChart,
    // CandlestickChart,
    // EffectScatterChart,
    // LinesChart,
    // HeatmapChart,
    // PictorialBarChart,
    // ThemeRiverChart,
    // SunburstChart,
    CustomChart,
} from 'echarts-6/charts'
// import components, all suffixed with Component
import {
    // GridSimpleComponent,
    GridComponent,
    // PolarComponent,
    // RadarComponent,
    // GeoComponent,
    // SingleAxisComponent,
    // ParallelComponent,
    // CalendarComponent,
    // GraphicComponent,
    ToolboxComponent,
    TooltipComponent,
    AxisPointerComponent,
    // BrushComponent,
    TitleComponent,
    // TimelineComponent,
    // MarkPointComponent,
    MarkLineComponent,
    MarkAreaComponent,
    LegendComponent,
    // LegendScrollComponent,
    // LegendPlainComponent,
    // DataZoomComponent,
    DataZoomInsideComponent,
    DataZoomSliderComponent,
    // VisualMapComponent,
    // VisualMapContinuousComponent,
    // VisualMapPiecewiseComponent,
    // AriaComponent,
    // TransformComponent,
    // DatasetComponent,
} from 'echarts-6/components'
// Import renderer, note that introducing the CanvasRenderer or SVGRenderer is a required step
import {
    CanvasRenderer,
    // SVGRenderer,
} from 'echarts-6/renderers'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    Grid,
    Popover,
    Stack,
} from '@mui/material'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import i18n from 'simple-react-i18n'
import NumberField from 'components/forms/NumberField'
import { removeNullKeys } from 'utils/StoreUtils'
import { exportExcelIcon, exportPictureIcon, fullScreenExitIcon, fullScreenIcon, swapVertical } from './EChartUtils'
import { flatMap, isArray, isNil, isNumber, isUndefined, range } from 'lodash'
import ExportAction from 'export/actions/ExportAction'
import { formatData } from 'utils/ExportDataUtil'
import { echartsDefaultBlue } from 'utils/constants/ColorTheme'

// Register the required components
echarts.use(
    [TitleComponent, TooltipComponent, AxisPointerComponent, GridComponent, ToolboxComponent, LineChart, BarChart, CustomChart, CanvasRenderer, MarkAreaComponent, MarkLineComponent, LegendComponent, DataZoomInsideComponent, DataZoomSliderComponent],
)

const DATAZOOM_HEIGHT = 20
const DATAZOOM_SPACE = 35 // hauteur du slider + marge

const overrideYAxisBounds = (yAxis, applied) => {
    const apply = (ax) => ({
        ...ax,
        ...(applied.min !== undefined ? { min: applied.min } : {}),
        ...(applied.max !== undefined ? { max: applied.max } : {}),
    })
    return isArray(yAxis) ? yAxis.map(apply) : apply(yAxis)
}

const EChartV6 = ({
    option,
    lazyUpdate = true,
    style,
    withToolbox = true,
    withYZoomModal = false,
    withToolFullScreen = false,
    withDataZoom = false,
    exportDatas,
    onChartReady,
    onEvents,
}) => {
    const dispatch = useDispatch()
    const containerRef = useRef(null)

    const [modalOpen, setModalOpen] = useState(false)
    const [tmpYZoom, setTmpYZoom] = useState({})
    const [appliedYZoom, setAppliedYZoom] = useState()
    const [exportPopoverOpen, setExportPopoverOpen] = useState(false)
    const [isFullScreen, setIsFullScreen] = useState(false)

    const finalOption = useMemo(() => {
        // la grille est remontée de la place prise par le slider (bottom par défaut d'ECharts : 60), un bottom non numérique est laissé tel quel
        const shiftGridBottom = grid => (isNil(grid.bottom) || isNumber(grid.bottom) ? { ...grid, bottom: (grid.bottom ?? 60) + DATAZOOM_SPACE } : grid)
        const xAxisCount = isArray(option?.xAxis) ? option.xAxis.length : 1
        const withZoom = withDataZoom && option ? {
            ...option,
            grid: isArray(option.grid) ? option.grid.map(shiftGridBottom) : shiftGridBottom(option.grid ?? {}),
            dataZoom: [
                ...(isNil(option.dataZoom) ? [] : flatMap([option.dataZoom])),
                {
                    type: 'slider',
                    xAxisIndex: range(xAxisCount),
                    filterMode: 'none',
                    bottom: 10,
                    height: DATAZOOM_HEIGHT,
                },
            ],
        } : option

        const formattedOption = appliedYZoom && withZoom?.yAxis ?
            { ...withZoom, yAxis: overrideYAxisBounds(withZoom.yAxis, appliedYZoom) }
            :
            withZoom
        if (!withToolbox) {
            return formattedOption
        }
        return {
            ...formattedOption,
            toolbox: {
                ...(formattedOption?.toolbox ?? {}),
                feature: {
                    ...(formattedOption?.toolbox?.feature ?? {}),
                    saveAsImage: {
                        title: i18n.pictureExport,
                        icon: exportPictureIcon,
                    },
                    myToolExport: {
                        show: !isUndefined(exportDatas),
                        title: i18n.export,
                        icon: exportExcelIcon,
                        iconStyle: {
                            color: echartsDefaultBlue,
                        },
                        onclick: () => {
                            setExportPopoverOpen(true)
                        },
                    },
                    myYZoom: {
                        show: withYZoomModal,
                        title: i18n.zoomVertical,
                        icon: swapVertical,
                        onclick: () => {
                            setTmpYZoom(appliedYZoom ?? {})
                            setModalOpen(true)
                        },
                    },
                    restore: {
                        title: i18n.restore,
                    },
                    myToolFullScreen: {
                        show: withToolFullScreen,
                        title: i18n.fullScreen,
                        icon: isFullScreen ? fullScreenExitIcon : fullScreenIcon,
                        onclick: () => setIsFullScreen(prev => !prev),
                    },
                },
            },
        }
    }, [option, appliedYZoom, withToolbox, withYZoomModal, withToolFullScreen, withDataZoom, exportDatas, isFullScreen])


    const apply = () => {
        setAppliedYZoom(removeNullKeys(tmpYZoom))
        setModalOpen(false)
    }

    const clean = () => {
        setAppliedYZoom(null)
        setTmpYZoom({})
        setModalOpen(false)
    }

    const applyFromEnter = (key, value) => {
        const next = { ...tmpYZoom, [key]: value }
        setTmpYZoom(next)
        setAppliedYZoom(removeNullKeys(next))
        setModalOpen(false)
    }

    const handleExport = (type) => {
        if (!exportDatas) return
        const body = exportDatas.datas.map(row => ({ ...row, headers: exportDatas.headers }))
        dispatch(ExportAction.export(formatData(body, type), type, exportDatas.fileName))
        setExportPopoverOpen(false)
    }

    const fullScreenContainer = (children) => (
        isFullScreen ? (
            <Dialog fullScreen open onClose={() => setIsFullScreen(false)}>
                {children}
            </Dialog>
        ) : children
    )

    const fullScreenStyle = isFullScreen ? { width: '100%', height: '100vh' } : {}

    return (
        fullScreenContainer(<>
            <Grid ref={containerRef}>
                <ReactEChartsCore
                    echarts={echarts}
                    option={finalOption}
                    notMerge={true}
                    lazyUpdate={lazyUpdate}
                    onChartReady={onChartReady}
                    onEvents={onEvents}
                    style={{ width: '100%', height: '100%', ...style, ...fullScreenStyle }}
                />
            </Grid>
            {withYZoomModal && (
                <Dialog open={modalOpen} onClose={() => setModalOpen(false)}>
                    <DefaultDialogTitle title={i18n.zoomVertical} onClose={() => setModalOpen(false)} />
                    <DialogContent>
                        <Grid container spacing={2}>
                            <Grid size={6}>
                                <NumberField
                                    title={i18n.min}
                                    value={tmpYZoom.min}
                                    onChange={v => setTmpYZoom({ ...tmpYZoom, min: v })}
                                    floatValue
                                    onEnterKeyPress={v => applyFromEnter('min', v)}
                                />
                            </Grid>
                            <Grid size={6}>
                                <NumberField
                                    title={i18n.max}
                                    value={tmpYZoom.max}
                                    onChange={v => setTmpYZoom({ ...tmpYZoom, max: v })}
                                    floatValue
                                    onEnterKeyPress={v => applyFromEnter('max', v)}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={clean} variant='outlined'>
                            {i18n.clean}
                        </Button>
                        <Button onClick={apply} variant='contained'>
                            {i18n.apply}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
            {exportDatas && (
                <Popover
                    open={exportPopoverOpen}
                    anchorEl={containerRef.current}
                    onClose={() => setExportPopoverOpen(false)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Stack direction='row' spacing={1} padding={1}>
                        <Button variant='contained' onClick={() => handleExport('xlsx')}>
                            {i18n.xlsx}
                        </Button>
                        <Button variant='contained' onClick={() => handleExport('csv')}>
                            {i18n.csv}
                        </Button>
                    </Stack>
                </Popover>
            )}
        </>)
    )
}

EChartV6.propTypes = {
    option: PropTypes.shape({
        yAxis: PropTypes.oneOfType([
            PropTypes.shape({}),
            PropTypes.arrayOf(PropTypes.shape({})),
        ]),
        toolbox: PropTypes.shape({}),
    }),
    lazyUpdate: PropTypes.bool,
    style: PropTypes.shape({}),
    withYZoomModal: PropTypes.bool,
    withToolFullScreen: PropTypes.bool,
    withToolbox: PropTypes.bool, // false : pas de toolbox (export image, zoom vertical, export de données)
    withDataZoom: PropTypes.bool, // slider de zoom horizontal, la grille est remontée automatiquement
    exportDatas: PropTypes.shape({
        fileName: PropTypes.string,
        datas: PropTypes.arrayOf(PropTypes.shape({})),
        headers: PropTypes.arrayOf(PropTypes.string),
    }),
    onChartReady: PropTypes.func,
    onEvents: PropTypes.objectOf(PropTypes.func),
}

export default EChartV6